import React, { useEffect, useState } from 'react';
import { lowStockAPI, purchaseAPI } from '@/lib/api';
import { EntityLink } from '@/components/EntityLink';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Download, Mail, AlertTriangle, Package, Loader2, ShoppingCart, Wrench, Users } from 'lucide-react';
import { toast } from 'sonner';

export default function LowStockReportPage() {
  const [mainTab, setMainTab] = useState('buy'); // 'buy', 'make', 'receive_from_customer'
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState('');
  const [emailingSupplier, setEmailingSupplier] = useState('');
  const [selectedItems, setSelectedItems] = useState({});
  const [addingToIndent, setAddingToIndent] = useState('');
  const [counts, setCounts] = useState({ buy: 0, make: 0, receive_from_customer: 0 });

  useEffect(() => { loadCounts(); }, []);
  useEffect(() => { loadReport(mainTab); }, [mainTab]);

  const loadCounts = async () => {
    try {
      // Load reports for each type to get counts
      const [buyRes, makeRes, receiveRes] = await Promise.all([
        lowStockAPI.getReportByMakeOrBuy('buy'),
        lowStockAPI.getReportByMakeOrBuy('make'),
        lowStockAPI.getReportByMakeOrBuy('receive_from_customer'),
      ]);
      setCounts({
        buy: buyRes.data.total_low_stock || 0,
        make: makeRes.data.total_low_stock || 0,
        receive_from_customer: receiveRes.data.total_low_stock || 0,
      });
    } catch { /* ignore */ }
  };

  const loadReport = async (makeOrBuy) => {
    setLoading(true);
    try {
      const res = await lowStockAPI.getReportByMakeOrBuy(makeOrBuy);
      setReport(res.data);
      setSelectedItems({});
    } catch { toast.error('Failed to load low stock report'); }
    finally { setLoading(false); }
  };

  const downloadPDF = async (supplierId, supplierName) => {
    const key = supplierId || 'tab';
    setDownloadingPdf(key);
    try {
      const res = await lowStockAPI.downloadPDF(supplierId, null, mainTab);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Low_Stock_${mainTab}_${(supplierName || 'All').replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch { toast.error('Failed to download PDF'); }
    finally { setDownloadingPdf(''); }
  };

  const emailSupplier = async (supplierId, supplierName) => {
    setEmailingSupplier(supplierId);
    try {
      const res = await lowStockAPI.emailSupplier(supplierId);
      if (res.data.sent) toast.success(`Email sent to ${supplierName}`);
      else toast.info('Email draft prepared (Resend API key not configured)');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send email'); }
    finally { setEmailingSupplier(''); }
  };

  const toggleItem = (itemId) => {
    setSelectedItems(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const toggleSupplierItems = (items) => {
    const allSelected = items.every(i => selectedItems[i.id]);
    const updated = { ...selectedItems };
    items.forEach(i => { updated[i.id] = !allSelected; });
    setSelectedItems(updated);
  };

  const addToIndent = async (item) => {
    setAddingToIndent(item.id);
    try {
      const shortage = Math.max(0, (item.min_stock_level || 0) - (item.total_stock || 0));
      await purchaseAPI.addToIndent({
        item_id: item.id,
        item_name: item.item_name,
        unique_id: item.unique_id,
        external_id: item.external_id || '',
        item_type: item.item_type || '',
        unit_of_measure: item.unit_of_measure || 'Nos',
        required_quantity: shortage,
        current_stock: item.total_stock || 0,
        min_stock_level: item.min_stock_level || 0,
        preferred_supplier_id: item.preferred_supplier_id || null,
        preferred_supplier_name: item.preferred_supplier_name || '',
        source: 'manual',
      });
      toast.success(`${item.item_name} added to indent`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add to indent'); }
    finally { setAddingToIndent(''); }
  };

  const addSelectedToIndent = async (supplierGroup) => {
    const items = supplierGroup.items.filter(i => selectedItems[i.id]);
    if (items.length === 0) { toast.error('No items selected'); return; }
    setAddingToIndent('bulk-' + (supplierGroup.supplier_id || 'none'));
    try {
      const payload = items.map(item => ({
        item_id: item.id,
        item_name: item.item_name,
        unique_id: item.unique_id,
        external_id: item.external_id || '',
        item_type: item.item_type || '',
        unit_of_measure: item.unit_of_measure || 'Nos',
        required_quantity: Math.max(0, (item.min_stock_level || 0) - (item.total_stock || 0)),
        current_stock: item.total_stock || 0,
        min_stock_level: item.min_stock_level || 0,
        preferred_supplier_id: item.preferred_supplier_id || null,
        preferred_supplier_name: item.preferred_supplier_name || '',
        source: 'manual',
      }));
      await purchaseAPI.addBulkToIndent(payload);
      toast.success(`${items.length} items added to indent`);
      setSelectedItems({});
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add to indent'); }
    finally { setAddingToIndent(''); }
  };

  const totalCount = counts.buy + counts.make + counts.receive_from_customer;
  const canAddToIndent = mainTab === 'buy'; // Only Buy items can be added to indent

  const getTabIcon = (tab) => {
    switch(tab) {
      case 'buy': return <ShoppingCart className="w-3.5 h-3.5 mr-1" />;
      case 'make': return <Wrench className="w-3.5 h-3.5 mr-1" />;
      case 'receive_from_customer': return <Users className="w-3.5 h-3.5 mr-1" />;
      default: return null;
    }
  };

  const getTabLabel = (tab) => {
    switch(tab) {
      case 'buy': return 'Buy Items';
      case 'make': return 'Make Items';
      case 'receive_from_customer': return 'Receive from Customer';
      default: return tab;
    }
  };

  const getEmptyMessage = () => {
    switch(mainTab) {
      case 'buy': return 'All purchased items are above minimum stock levels. No purchase action needed.';
      case 'make': return 'All manufactured items are above minimum stock levels. No production action needed.';
      case 'receive_from_customer': return 'All customer-supplied items are above minimum stock levels.';
      default: return 'No items below minimum stock.';
    }
  };

  return (
    <div className="space-y-4" data-testid="low-stock-report-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" /> Low Stock Report
          </h1>
          <p className="text-xs text-muted-foreground">
            {totalCount} items below minimum stock level
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => { loadCounts(); loadReport(mainTab); }} data-testid="refresh-report-btn">
            Refresh
          </Button>
          {canAddToIndent && (
            <Button size="sm" onClick={() => downloadPDF(null, 'All_Suppliers')} disabled={downloadingPdf === 'tab'} data-testid="download-tab-pdf-btn">
              {downloadingPdf === 'tab' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}
              Download PDF
            </Button>
          )}
        </div>
      </div>

      {/* Main Tabs: Buy / Make / Receive from Customer */}
      <Tabs value={mainTab} onValueChange={setMainTab} className="w-full">
        <TabsList className="bg-slate-100" data-testid="make-or-buy-tabs">
          <TabsTrigger value="buy" className="flex items-center" data-testid="tab-buy">
            {getTabIcon('buy')} Buy ({counts.buy})
          </TabsTrigger>
          <TabsTrigger value="make" className="flex items-center" data-testid="tab-make">
            {getTabIcon('make')} Make ({counts.make})
          </TabsTrigger>
          <TabsTrigger value="receive_from_customer" className="flex items-center" data-testid="tab-receive">
            {getTabIcon('receive_from_customer')} Receive ({counts.receive_from_customer})
          </TabsTrigger>
        </TabsList>

        <div className="mt-4">
          {/* Info banner for non-buy items */}
          {mainTab !== 'buy' && (
            <Card className={`mb-4 ${mainTab === 'make' ? 'bg-purple-50 border-purple-200' : 'bg-blue-50 border-blue-200'}`}>
              <CardContent className="p-3">
                <p className={`text-xs ${mainTab === 'make' ? 'text-purple-700' : 'text-blue-700'}`}>
                  {mainTab === 'make' 
                    ? 'These items are manufactured in-house. Create a Work Order to replenish stock.' 
                    : 'These items are received from customers. Coordinate with customers for replenishment.'}
                </p>
              </CardContent>
            </Card>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">Loading report...</div>
          ) : (!report?.suppliers || report.suppliers.length === 0) ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Package className="w-10 h-10 text-emerald-400 mb-3" />
                <p className="text-sm font-medium text-emerald-700">All clear!</p>
                <p className="text-xs text-muted-foreground mt-1 text-center max-w-md">{getEmptyMessage()}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {report.suppliers.map((group, gi) => {
                const groupSelected = group.items.filter(i => selectedItems[i.id]);
                const allGroupSelected = group.items.length > 0 && group.items.every(i => selectedItems[i.id]);
                return (
                  <Card key={group.supplier_id || `no-sup-${gi}`} data-testid={`supplier-group-${group.supplier_id || 'none'}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <CardTitle className="text-sm font-semibold">{group.supplier_name}</CardTitle>
                          <p className="text-xs text-muted-foreground">{group.items.length} items below minimum</p>
                        </div>
                        <div className="flex gap-1.5 flex-wrap">
                          {/* Add to Indent - only for Buy items */}
                          {canAddToIndent && groupSelected.length > 0 && (
                            <Button size="sm" variant="default" className="h-7 text-xs bg-cyan-600 hover:bg-cyan-700"
                              onClick={() => addSelectedToIndent(group)}
                              disabled={addingToIndent === `bulk-${group.supplier_id || 'none'}`}
                              data-testid={`add-selected-indent-${group.supplier_id || 'none'}`}>
                              {addingToIndent === `bulk-${group.supplier_id || 'none'}` ?
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ShoppingCart className="w-3 h-3 mr-1" />}
                              Add {groupSelected.length} to Indent
                            </Button>
                          )}
                          {canAddToIndent && (
                            <>
                              <Button size="sm" variant="outline" className="h-7 text-xs"
                                onClick={() => downloadPDF(group.supplier_id, group.supplier_name)}
                                disabled={downloadingPdf === (group.supplier_id || 'none')}
                                data-testid={`download-pdf-${group.supplier_id || 'none'}`}>
                                {downloadingPdf === (group.supplier_id || 'none') ?
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                                PDF
                              </Button>
                              {group.supplier_id && (
                                <Button size="sm" className="h-7 text-xs"
                                  onClick={() => emailSupplier(group.supplier_id, group.supplier_name)}
                                  disabled={emailingSupplier === group.supplier_id}
                                  data-testid={`email-supplier-${group.supplier_id}`}>
                                  {emailingSupplier === group.supplier_id ?
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Mail className="w-3 h-3 mr-1" />}
                                  Email
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs" data-testid={`low-stock-table-${group.supplier_id || 'none'}`}>
                          <thead>
                            <tr className="border-b bg-muted/50">
                              {canAddToIndent && (
                                <th className="p-2.5 w-8">
                                  <Checkbox
                                    checked={allGroupSelected}
                                    onCheckedChange={() => toggleSupplierItems(group.items)}
                                    data-testid={`select-all-${group.supplier_id || 'none'}`}
                                  />
                                </th>
                              )}
                              <th className="text-left p-2.5 font-medium text-muted-foreground">Item Code</th>
                              <th className="text-left p-2.5 font-medium text-muted-foreground">Item Name</th>
                              <th className="text-left p-2.5 font-medium text-muted-foreground">Type</th>
                              <th className="text-left p-2.5 font-medium text-muted-foreground">UOM</th>
                              <th className="text-center p-2.5 font-medium text-muted-foreground">Current</th>
                              <th className="text-center p-2.5 font-medium text-muted-foreground">Min</th>
                              <th className="text-center p-2.5 font-medium text-muted-foreground">Shortage</th>
                              {canAddToIndent && (
                                <th className="text-center p-2.5 font-medium text-muted-foreground">Status</th>
                              )}
                              {canAddToIndent && (
                                <th className="text-center p-2.5 font-medium text-muted-foreground">Indent</th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {group.items.map(item => {
                              const shortage = Math.max(0, (item.min_stock_level || 0) - (item.total_stock || 0));
                              return (
                                <tr key={item.id} className="border-b hover:bg-muted/30">
                                  {canAddToIndent && (
                                    <td className="p-2.5">
                                      <Checkbox
                                        checked={!!selectedItems[item.id]}
                                        onCheckedChange={() => toggleItem(item.id)}
                                        disabled={!!item.pending_po || !!item.pending_indent}
                                        data-testid={`select-item-${item.id}`}
                                      />
                                    </td>
                                  )}
                                  <td className="p-2.5 font-mono font-medium"><EntityLink type="item" id={item.item_id} className="font-mono font-medium text-xs">{item.unique_id}</EntityLink></td>
                                  <td className="p-2.5"><EntityLink type="item" id={item.item_id} className="text-xs">{item.item_name}</EntityLink></td>
                                  <td className="p-2.5 text-muted-foreground">{item.item_type || '-'}</td>
                                  <td className="p-2.5">{item.unit_of_measure || 'Nos'}</td>
                                  <td className="p-2.5 text-center">
                                    <Badge variant="outline" className="text-[10px] text-red-600 border-red-200 bg-red-50">
                                      {item.total_stock || 0}
                                    </Badge>
                                  </td>
                                  <td className="p-2.5 text-center font-medium">{item.min_stock_level}</td>
                                  <td className="p-2.5 text-center">
                                    <span className="font-bold text-amber-600">{shortage}</span>
                                  </td>
                                  {canAddToIndent && (
                                    <td className="p-2.5 text-center">
                                      {item.pending_po ? (
                                        <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200" data-testid={`po-badge-${item.id}`}>
                                          PO {item.pending_po.status?.replace(/_/g, ' ')}
                                          {item.pending_po.po_number ? ` (${item.pending_po.po_number})` : ''}
                                        </Badge>
                                      ) : item.pending_indent ? (
                                        <Badge variant="outline" className="text-[10px] bg-cyan-50 text-cyan-700 border-cyan-200" data-testid={`indent-badge-${item.id}`}>
                                          In Indent ({item.pending_indent.status?.replace(/_/g, ' ')})
                                        </Badge>
                                      ) : (
                                        <span className="text-[10px] text-muted-foreground">—</span>
                                      )}
                                    </td>
                                  )}
                                  {canAddToIndent && (
                                    <td className="p-2.5 text-center">
                                      {item.pending_po || item.pending_indent ? (
                                        <span className="text-[10px] text-muted-foreground">Already in pipeline</span>
                                      ) : (
                                        <Button size="sm" variant="ghost" className="h-6 text-[10px] text-cyan-700 hover:bg-cyan-50"
                                          onClick={() => addToIndent(item)}
                                          disabled={addingToIndent === item.id}
                                          data-testid={`add-indent-${item.id}`}>
                                          {addingToIndent === item.id ?
                                            <Loader2 className="w-3 h-3 animate-spin" /> : <ShoppingCart className="w-3 h-3" />}
                                        </Button>
                                      )}
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </Tabs>
    </div>
  );
}
